import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useNavigate, useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import CloseIcon from "@mui/icons-material/Close";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tooltip from "@mui/material/Tooltip";
import Tabs, { tabsClasses } from "@mui/material/Tabs";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import NamePanel from "./Panels/NamePanel";
import JobHistory from "./Panels/HistoryPanel";
import RunTimePanel from "./Panels/RunTimePanel";
import SchedulePanel from "./Panels/Schedule";
import DataSourceList from "./Panels/DataSourcePanel";
import SourceDestination from "./Panels/SourceDestination";
import TransformPanel from "./Panels/TransformPanel";
import { ButtonUI, ConfirmPopup } from "../../../../Common";
import NotAllowedDialog from "../../../../Common/NotAllowedDialog";
import RoutingPanel from "./Panels/RoutingPanel";

import { handleEditJob, handlePipelineList, saveJobDetails } from "../../../../../store/actions/datapipeline";
import { showToast } from "../../../../../store/actions";

import DataSource from "../../../../../service/dataSource";
import WorkspaceService from "../../../../../service/workspace";
import DataPipelineService from "../../../../../service/dataPipelineService";

import {
  CONVERSION_FROM_SAS,
  DETAUL_CRON_VALUE,
  JOB_PURPOSE_CONVERSION,
  JOB_PURPOSE_EXCHANGE,
  MESSAGES,
  TABS_BY_JOB_TYPE,
} from "../../utils/JobData";
import {
  arrangeSchema,
  base64Decode,
  base64Encode,
  checkIsClusterExist,
  checkIsSchemaExist,
  getUUID,
  isEqual,
  setPrevState,
} from "../../../../../util/common";
import { OBJECT_TYPE } from "../../../../../util/Data/DataModeling";
import { validate } from "./validator";
import { navScheduledJobs } from "../../../../kmk/AppData";
import SQLService from "../../../../../service/mysqlService";
import NotebookPanel from "./Panels/NotebookPanel";
import SageMakerService from "../../../../../service/sageMakerService";
import Salesforce from "../../../../../service/salesforce";
import Veevavault from "../../../../../service/veevavault";
import DataQualityQuery from "./Panels/DataQualityQuery";
import EmailContent from "./Panels/EmailContentPanel";
import AddIcon from "@mui/icons-material/Add";
// import AdditionalDatapanel from "./Panels/AdditionalDatapanel";
import AdditionalDatapanel from "../../Notification/AdditionalData";

/* 
  NOTE 
  ---------------------- 
  1. If DEV add new required variable then need to add validation in validator file
  2. If DEV changes name of any required variable make appropriate changes in validator file
  3. If new variable is added for which we need to track changes then at that moment update JobData.js file accordingly and set the value in respective tab ref variable and set the value as well in useEffect 
  ---------------------- 
*/

const useStyles = makeStyles()((theme) => ({
  PanelContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  PanelHeader: {
    width: "100%",
    margin: "0",
    padding: "0",
    height: "fit-content",
    position: "sticky",
    top: "0",
    backgroundColor: "white",
    zIndex: "999",
  },
  AppBar: {
    position: "sticky",
    top: "0",
    zIndex: "999",
    boxShadow: 0,
    backgroundColor: "#fff",
  },
  TabContainer: {
    display: "flex",
    margin: "0",
    padding: "0",
    alignItems: "center",
  },
  Tabs: {
    flex: "1 1 auto",
    [`& .${tabsClasses.scrollButtons}`]: {
      backgroundColor: "#ddcdcd",
      color: "black",
      "&.Mui-disabled": { display: "none" },
    },
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div hidden={value !== index} {...other}>
      {value === index && children}
    </div>
  );
}

const DEFAULT_MAX_FILE_SIZE = 500;

const DATA_MODELING_JOBS = [1, 4, 5, 6];

const FIRST_KEY = getUUID();

const EditPanel = React.forwardRef(({ loading, setLoading, canvasRef = {} }, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { Workspace_id, DataPipeline_id } = useParams();

  const { classes } = useStyles();

  const dataPipeline = useSelector((state) => state.dataPipeline);
  const workspace = useSelector((state) => state.workspace);
  const loggedInUser = useSelector((state) => state.user);
  const clusterList = useSelector((state) => state.clusterList);
  const { defaultSourceValues, defaultCluster, delimiters, salesforceDelimiters, timezone, nullValues, defaultSchema } =
    useSelector((state) => state.config);

  const IsDataVaultEnabled = useMemo(
    () => workspace?.workspace?.IsDataVaultEnabled || false,
    [workspace?.workspace?.IsDataVaultEnabled]
  );
  const accessRole = useMemo(() => workspace?.workspace?.accessRole || 1, [workspace?.workspace?.accessRole]);
  const DBName = useMemo(() => workspace?.workspace?.DBName || 1, [workspace?.workspace?.DBName]);
  const user = useMemo(() => loggedInUser.currentUser, [loggedInUser.currentUser]);

  const { editJob, dataPipelineList } = useMemo(() => dataPipeline, [dataPipeline]);
  const currentUserFullName = useMemo(() => `${user.FirstName} ${user.LastName}`, [user.FirstName, user.LastName]);
  const jobType = useMemo(() => Number(editJob?.data?.type || 1), [editJob?.data?.type]);

  const nameTabRef = useRef({});
  const datasetTabRef = useRef({});
  const transformTabRef = useRef({
    location: "SNOWFLAKE",
    Type: "SQL",
    query: "",
    database: "",
    schema: "",
  });
  const queryTabRef = useRef({
    query: "",
  });
  const notebookTabRef = useRef({});
  const actionTabRef = useRef({});
  const scheduleTabRef = useRef({});
  const runTimeTabRef = useRef({});
  const notificationTabRef = useRef({});
  const additionalDataSectionsRef = useRef({});

  // These all ref stores the previous state of the given tab, so while editing the job we can compare either user has made any changes or not
  const refs = useMemo(() => {
    return {
      Name: nameTabRef,
      DataSets: datasetTabRef,
      Entities: datasetTabRef,
      DataQuality: queryTabRef,
      Transform: transformTabRef,
      Condition: transformTabRef,
      Notebook: notebookTabRef,
      Action: actionTabRef,
      Schedule: scheduleTabRef,
      Runtime: runTimeTabRef,
      EmailContent: notificationTabRef,
      AdditionalData: additionalDataSectionsRef,
    };
  }, []);

  const participantsExcludeVisitor = useRef([]);
  const restrictedObject = useRef({
    schema: "",
    table: "",
  });

  const {
    compression: defaultCompression,
    type: defaultFileType,
    delimiter: defaultDelimiter,
    encoding: defaultEncoding,
    qualifier: defaultFieldEnclosedBy,
    errorAction: defaultErrorAction,
    nullValue: defaultNullValue,
  } = useMemo(() => defaultSourceValues, [defaultSourceValues]);

  // State for current Tab
  const [value, setValue] = useState("Name");

  // Common State for name panel
  const [nameState, setNameState] = useState({
    name: editJob?.data?.label || "",
    DataPipeline_id: DataPipeline_id || -1,
    description: "",
  });

  // Common State for Datasets panel (For Job type 1,4,5,6)
  const [dataSets, setDataSets] = useState([]);

  // Common State for runtime panel
  const [extSnowflakeClusterList, setExtSnowflakeClusterList] = useState([]);
  const [runTimeState, setRunTimeState] = useState({
    cluster: defaultCluster,
    runJobAsUserId: user.User_id,
    runJobAsUserFullName: currentUserFullName,
  });

  // Common state for Schedule Panel
  const [scheduleState, setScheduleState] = useState({
    scheduleEnabled: false,
    nextRunTime: "",
    timeZone: timezone || "UTC",
  });
  const cronScheduleRef = useRef(DETAUL_CRON_VALUE);

  // Specifically for Quick import jobs
  const [previousState, setPreviousState] = useState({
    destination: "",
    delimiter: defaultDelimiter,
    hasHeaderRow: true,
    encoding: defaultEncoding,
    fieldEnclosedBy: defaultFieldEnclosedBy,
    fields: [],
    schemaName: "",
    groupBy: "",
    tableName: "",
    destinationAction: "new",
    existingTableAction: "overwrite",
    source: "",
    sourcePath: "",
    nullValue: defaultNullValue,
  });

  const [closeConfirmation, setCloseConfirmation] = useState({
    open: false,
    nextNodeToEdit: null,
  });

  const [dataQualityJobState, setDataQualityJobState] = useState({
    query: "",
    queryError: "",
  });

  const [exchangeJobState, setExchangeJobState] = useState({
    jobPurpose: JOB_PURPOSE_EXCHANGE,
    locationList: [],
    sourceType: "",
    bucket: "",
    sourcePath: "",
    source: "",
    targetPath: "",
    destination: "",
    container: "",
    desinationContainer: "",
    docuList: "",
    mapping: false,
    delimiter: defaultDelimiter,
    customDelimeter: "",
    hasHeaderRow: true,
    encoding: defaultEncoding,
    fieldEnclosedBy: defaultFieldEnclosedBy,
    schemaName: "",
    groupBy: "",
    tableName: "",
    destinationAction: "new",
    existingTableAction: "overwrite",
    fields: [],
    sourcePathError: "",
    queryError: "",
    query: "",
    filename: "",
    fileType: defaultFileType,
    loadingDropBoxValues: false,
    resetFiles: false,
    isFieldsConfigured: false,
    allSchemas: [],
    snowflakeSchemas: [],
    externalSnowflakeSchemas: [],
    tableList: [],
    isTableLoaded: false,
    compression: defaultCompression,
    singleFile: false,
    maxFileSize: DEFAULT_MAX_FILE_SIZE,
    onError: defaultErrorAction,
    onErrorNumber: 0,
    skipFileError: "",
    nullValue: defaultNullValue,
    customNullValue: "",
    conversionFrom: CONVERSION_FROM_SAS,
    externalIdFieldName: "",
    renameFileRegex: false,
    unzipFiles: false,
    regexMatchPattern: "",
    regexReplacePattern: "",
  });

  const [transformJobState, setTransformJobState] = useState({
    Type: "SQL",
    query: "",
    queryError: "",
    queryLastUpdatedByName: "",
    database: "",
    schema: "",
    location: "SNOWFLAKE",
    locationList: [],
  });

  const [notebookJobState, setNotebookJobState] = useState({
    type: "sagemaker_pipeline",
    jobPipeline: "",
    pipelineList: [],
  });

  const [notificationState, setNotificationState] = useState({
    locationList: [],
    name: "",
    jobType: "",
    Type: "email",
    timeZone: "",
    nextRunTime: "",
    loading: false,
    scheduleEnabled: false,
    closeSelect: false,
    DataPipeline_id: "",
    isEntryJob: false,
    subject: "",
    body: "",
    emails: [],
  });

  const locationSchemasRef = useRef({});
  const locationClustersRef = useRef({ SNOWFLAKE: clusterList });
  const [additionalDataSections, setAdditionalDataKeys] = useState([]);
  const [selectedKey, setSelectedKey] = useState(FIRST_KEY);

  const [variableError, setVariableError] = useState(() => ({
    [FIRST_KEY]: { error: false, message: "", schemaError: "" },
  }));

  const [schemas, setSchemas] = useState(null);
  const [fieldLoading, setFieldLoading] = useState(false);
  const [schemaError, setSchemaError] = useState(false);
  const [clusterError, setClusterError] = useState(false);

  const [notAllowedDialog, setNotAllowedDialog] = useState({
    confirmMessage: "",
    title: "",
    openConfirm: false,
  });

  const [jobReassignment, setJobReassignment] = useState({
    openConfirm: false,
    confirmMessage: "",
    loadingForConfirm: false,
  });

  const [isChanged, setIsChanged] = useState({
    Name: false,
    DataSets: false,
    Schedule: false,
    Runtime: false,
    Notebook: false,
  });

  const selectedSectionIndex = useMemo(
    () => additionalDataSections.findIndex((item) => item?.key === selectedKey),
    [additionalDataSections, selectedKey]
  );

  const addNewSection = useMemo(
    () => ({
      runJobAsUserId: user.User_id,
      runJobAsUserFullName: currentUserFullName,
      database: DBName || "",
      schema: defaultSchema || "",
      Cluster: defaultCluster || "",
      includeResult: false,
      queryResultAs: 1,
      queryLastUpdatedByName: "",
      query: "",
      queryError: "",
      location: "SNOWFLAKE",
    }),
    [DBName, currentUserFullName, defaultCluster, defaultSchema, user.User_id]
  );

  const { variableNames, fileNames } = useMemo(
    () =>
      additionalDataSections.reduce(
        (acc, item) => {
          if (item?.variable?.trim()) acc.variableNames.push(item.variable.trim().toLowerCase());
          if (item?.fileName?.trim()) acc.fileNames.push(item.fileName.trim().toLowerCase());
          return acc;
        },
        { variableNames: [], fileNames: [] }
      ),
    [additionalDataSections]
  );

  const destinationType = useMemo(
    () =>
      exchangeJobState.locationList
        .find(({ Stage_id }) => Stage_id === exchangeJobState.destination)
        ?.Detail?.type?.toLowerCase() || "",
    [exchangeJobState.destination, exchangeJobState.locationList]
  );

  const isExternalSnowflake = useMemo(
    () =>
      (exchangeJobState.sourceType?.toLowerCase() === "snowflake" &&
        exchangeJobState?.source?.toString() !== "SNOWFLAKE") ||
      (destinationType === "snowflake" && exchangeJobState?.destination?.toString() !== "SNOWFLAKE"),
    [destinationType, exchangeJobState?.destination, exchangeJobState?.source, exchangeJobState.sourceType]
  );

  const selectAll = useMemo(() => dataSets.every((item) => item.checked), [dataSets]);
  const intermediate = useMemo(() => !selectAll && dataSets.some((item) => item.checked), [selectAll, dataSets]);

  // Either we are creating the job or editing
  const inCreateMode = useMemo(() => editJob?.id?.startsWith("dndnode_") || false, [editJob.id]);

  const getSchemaList = useCallback(
    async (location = "SNOWFLAKE") => {
      if (jobType === 9) {
        const availableSchemas = locationSchemasRef.current[location]?.rawSchemas;
        if (!!availableSchemas) return availableSchemas;
      }

      setFieldLoading(true);
      const wsService = new WorkspaceService();
      let response;

      if (location === "SNOWFLAKE") {
        response = await wsService.getSchemaListByUser();
      } else {
        response = await wsService.getExternalSnowflakeSchemas(Workspace_id, location, {
          schemasOnly: true,
        });
      }

      const { success, data } = response;

      if (success) {
        const schemas = arrangeSchema(data, (schemasByDB) => {
          setSchemas(schemasByDB);
          if (jobType === 9) locationSchemasRef.current[location] = { schemas: schemasByDB, rawSchemas: data };
        });

        setFieldLoading(false);
        return schemas;
      }
      setFieldLoading(false);
      return {};
    },
    [Workspace_id, jobType]
  );

  const checkGivenTabChanges = useCallback(
    (tabID = "Name", jobType = 1) => {
      const fieldsToCompare = TABS_BY_JOB_TYPE[jobType].find((tab) => tab.id === tabID).fieldsToCompare;

      const states = {
        Name: nameState,
        DataSets: dataSets,
        Entities: dataSets,
        DataQuality: dataQualityJobState,
        Runtime: runTimeState,
        Notebook: notebookJobState,
        Action: exchangeJobState,
        Schedule: scheduleState,
        Transform: transformJobState,
        Condition: transformJobState,
        EmailContent: notificationState,
        AdditionalData: additionalDataSections,
      };

      let currentObject = {};
      let refObject = {};

      if (tabID === "DataSets" || tabID === "Entities") {
        currentObject = states[tabID];
        refObject = { ...refs[tabID].current };
      } else if (tabID === "Schedule") {
        currentObject = { scheduleEnabled: states[tabID].scheduleEnabled, schedule: cronScheduleRef.current };
        refObject = {
          scheduleEnabled: refs.Schedule.current.scheduleEnabled,
          schedule: refs.Schedule.current.schedule,
        };
      } else if (tabID === "AdditionalData") {
        const pick = (obj, keys) => Object.fromEntries(keys.map((k) => [k, obj?.[k]]));

        let tabChanged = false;
        const currentObject = states[tabID];
        for (const section of currentObject) {
          if (tabChanged) break;
          const currentRef = refs[tabID].current?.[section.key];
          tabChanged = !isEqual(pick(currentRef, fieldsToCompare), pick(section, fieldsToCompare));
        }

        setPrevState(setIsChanged, { [tabID]: tabChanged });
        return tabChanged;
      } else {
        fieldsToCompare.forEach((field) => {
          currentObject[field] = states[tabID][field];
          refObject[field] = refs[tabID].current?.[field];
        });
      }

      setPrevState(setIsChanged, { [tabID]: !isEqual(refObject, currentObject) });
      return !isEqual(refObject, currentObject);
    },
    [
      additionalDataSections,
      dataQualityJobState,
      dataSets,
      exchangeJobState,
      nameState,
      notebookJobState,
      notificationState,
      refs,
      runTimeState,
      scheduleState,
      transformJobState,
    ]
  );

  // To clean-up the confirmation popup related details and close it
  const handleCloseConfirmation = useCallback(() => {
    setPrevState(setCloseConfirmation, { open: false, nextNodeToEdit: null });
  }, []);

  // Click OK on confirmation popup
  const handleOkClick = useCallback(
    async (node = null) => {
      if (inCreateMode) canvasRef.current.removeEditingNode(editJob.id);
      setPrevState(setCloseConfirmation, { open: false, nextNodeToEdit: null });
      await dispatch(handleEditJob(null));
      if (node !== null) await dispatch(handleEditJob(node));
    },
    [canvasRef, dispatch, editJob.id, inCreateMode]
  );

  // Whether to show user confirmation or not
  const restrictOrNot = useCallback(() => {
    if (!editJob?.id) return false;

    let isChangeOnAnyTab = false;
    Object.keys(isChanged).forEach((tab) => {
      if (isChanged[tab]) isChangeOnAnyTab = true;
    });

    if (inCreateMode || (isChangeOnAnyTab && !isChanged[value])) return true;
    else if (!inCreateMode && value !== "History" && checkGivenTabChanges(value, editJob?.data?.type || 1)) return true;
    return false;
  }, [checkGivenTabChanges, editJob?.data?.type, editJob?.id, inCreateMode, isChanged, value]);

  // Close the edit panel or update the exisiting data with other  job data
  const handleCloseOrReplacePanel = useCallback(
    async (e, node = null) => {
      if (restrictOrNot()) setPrevState(setCloseConfirmation, { open: true, nextNodeToEdit: node });
      else {
        await dispatch(handleEditJob(null));
        if (node !== null) await dispatch(handleEditJob(node));
      }
    },
    [dispatch, restrictOrNot]
  );

  // Change the tab and while changing the tab check if is there any changes made on previous tab
  const handleChange = useCallback(
    (e, newValue) => {
      setValue((prevValue) => {
        if (!inCreateMode && prevValue !== "History") checkGivenTabChanges(prevValue, editJob?.data?.type || 1);
        return newValue;
      });
    },
    [checkGivenTabChanges, editJob?.data?.type, inCreateMode]
  );

  const compareQuery = useCallback((oldQuery = "", newQuery = "") => {
    oldQuery = oldQuery.trim().toLowerCase();
    newQuery = newQuery.trim().toLowerCase();
    return oldQuery !== newQuery;
  }, []);

  const handleDataModelingSave = useCallback(
    async (DataPipeline_id) => {
      const wsService = new WorkspaceService();
      const selectedObjects = dataSets.filter((source) => source.checked);
      const selectAll = selectedObjects.length === dataSets.length;

      const submitData = {
        name: nameState.name.trim(),
        description: nameState?.description?.trim() || "",
        type: jobType,
        DataPipeline_id,
        schedule: cronScheduleRef.current || DETAUL_CRON_VALUE,
        scheduleEnabled: scheduleState.scheduleEnabled || false,
        cluster: runTimeState.cluster,
        ...(jobType === 5 || jobType === 6
          ? { entity: { selectAll, entities: selectAll ? [] : selectedObjects.map(({ name }) => name) } }
          : { dataSource: { selectAll, dataSources: selectAll ? [] : selectedObjects.map(({ name }) => name) } }),
        fromEditPanel: true,
        ...(inCreateMode ? { position: editJob.position } : {}),
        ...(jobType === 4 ? { query: dataQualityJobState.query } : {}),
      };

      const response = inCreateMode
        ? await wsService.createDataModelingJob(submitData, Workspace_id, editJob?.data?.type || 1)
        : await wsService.updateDataModelingJob(submitData, Workspace_id, editJob.id, editJob?.data?.type || 1);

      //Updating query ref
      if (jobType === 4) queryTabRef.current.query = dataQualityJobState.query;

      return response;
    },
    [
      dataSets,
      nameState.name,
      nameState?.description,
      jobType,
      scheduleState.scheduleEnabled,
      runTimeState.cluster,
      inCreateMode,
      editJob.position,
      editJob?.data?.type,
      editJob.id,
      dataQualityJobState.query,
      Workspace_id,
    ]
  );

  const handleDataExchangeSave = useCallback(
    async (DataPipeline_id) => {
      let response = null;
      const submitData = {
        jobPurpose: exchangeJobState.jobPurpose,
        name: nameState.name.trim(),
        description: nameState?.description?.trim() || "",
        DataPipeline_id: DataPipeline_id,
        source_stage_id: exchangeJobState.source,
        dest_stage_id: exchangeJobState.destination,
        ...(isExternalSnowflake
          ? { database: exchangeJobState.groupBy || "", Cluster: runTimeState.cluster || "" }
          : {}),
        location: ["mysql", "salesforce", "snowflake", "veevavault"].includes(destinationType)
          ? exchangeJobState.source
          : exchangeJobState.targetPath,
        schedule: cronScheduleRef.current || DETAUL_CRON_VALUE,
        scheduleEnabled: scheduleState.scheduleEnabled || false,
        files: exchangeJobState.sourcePath?.split(",/").map((item, key) => (key !== 0 ? "/" + item : item)) || [],
        query: exchangeJobState.query,
        filename: exchangeJobState.filename,
        filetype: exchangeJobState.fileType,
        ...(inCreateMode ? { position: editJob.position } : {}),
        ...(exchangeJobState.fileType === "csv"
          ? {
              delimiter:
                exchangeJobState.delimiter === "custom" ? exchangeJobState.customDelimeter : exchangeJobState.delimiter,
              fieldEnclosedBy: exchangeJobState.fieldEnclosedBy,
              encoding: exchangeJobState.encoding,
              hasHeaderRow: exchangeJobState.hasHeaderRow,
            }
          : {
              delimiter: defaultDelimiter,
              fieldEnclosedBy: defaultFieldEnclosedBy,
              encoding: defaultEncoding,
              hasHeaderRow: true,
            }),
        Workspace_id,
        cluster: defaultCluster,
        schemaName: exchangeJobState.schemaName,
        tableName: exchangeJobState.tableName,
        append: exchangeJobState.destinationAction === "existing" && exchangeJobState.existingTableAction === "append",
        fields: exchangeJobState.fields,
        bucket: exchangeJobState.bucket,
        path: exchangeJobState.sourcePath,
        runJobAsUserId: runTimeState.runJobAsUserId,
        runJobAsUserFullName: runTimeState.runJobAsUserFullName,
        resetFiles: exchangeJobState?.resetFiles || false,
        fromEditPanel: true,
        compression: exchangeJobState.compression,
        singleFile: exchangeJobState.singleFile,
        maxFileSize: parseInt(exchangeJobState.maxFileSize),
        externalIdFieldName: exchangeJobState.externalIdFieldName,
        nullValue:
          (exchangeJobState.nullValue === "custom" ? exchangeJobState.customNullValue : exchangeJobState.nullValue) ||
          defaultNullValue,
        onError: exchangeJobState.onError,
        onErrorNumber: exchangeJobState.onErrorNumber,
        conversionFrom: exchangeJobState.conversionFrom || CONVERSION_FROM_SAS,
        renameFileRegex: exchangeJobState.renameFileRegex,
        unzipFiles: exchangeJobState.unzipFiles,
        ...(exchangeJobState.renameFileRegex
          ? {
              regexMatchPattern: exchangeJobState.regexMatchPattern,
              regexReplacePattern: exchangeJobState.regexReplacePattern,
            }
          : {
              regexMatchPattern: "",
              regexReplacePattern: "",
            }),
      };

      const wsService = new WorkspaceService();
      if (inCreateMode) response = await wsService.createExchangeJob(submitData, Workspace_id);
      else response = await wsService.updateExchangeJob(submitData, Workspace_id, editJob.id);

      actionTabRef.current = {
        jobPurpose: exchangeJobState.jobPurpose,
        sourceType: exchangeJobState.sourceType,
        bucket: exchangeJobState.bucket,
        sourcePath: exchangeJobState.sourcePath,
        source: exchangeJobState.source,
        targetPath: exchangeJobState.targetPath,
        destination: exchangeJobState.destination,
        docuList: exchangeJobState.docuList,
        delimiter: exchangeJobState.delimiter,
        customDelimeter: exchangeJobState.customDelimeter,
        nullValue: exchangeJobState.nullValue,
        customNullValue: exchangeJobState.customNullValue,
        hasHeaderRow: exchangeJobState.hasHeaderRow,
        encoding: exchangeJobState.encoding,
        fieldEnclosedBy: exchangeJobState.fieldEnclosedBy,
        groupBy: exchangeJobState.groupBy,
        schemaName: exchangeJobState.schemaName,
        tableName: exchangeJobState.tableName,
        destinationAction: exchangeJobState.destinationAction,
        existingTableAction: exchangeJobState.existingTableAction,
        fields: exchangeJobState.fields,
        query: exchangeJobState.query,
        filename: exchangeJobState.filename,
        fileType: exchangeJobState.fileType,
        resetFiles: exchangeJobState?.resetFiles,
        onErrorNumber: exchangeJobState.onErrorNumber,
        conversionFrom: exchangeJobState.conversionFrom,
        externalIdFieldName: exchangeJobState.externalIdFieldName,
        regexMatchPattern: exchangeJobState.regexMatchPattern,
        regexReplacePattern: exchangeJobState.regexReplacePattern,
      };

      runTimeTabRef.current = {
        ...(runTimeState.cluster !== undefined ? { cluster: runTimeState.cluster } : {}),
        ...(runTimeState.runJobAsUserId !== undefined ? { runJobAsUserId: runTimeState.runJobAsUserId } : {}),
        ...(runTimeState.runJobAsUserFullName !== undefined
          ? { runJobAsUserFullName: runTimeState.runJobAsUserFullName }
          : {}),
      };

      return response;
    },
    [
      exchangeJobState.jobPurpose,
      exchangeJobState.source,
      exchangeJobState.destination,
      exchangeJobState.groupBy,
      exchangeJobState.targetPath,
      exchangeJobState.sourcePath,
      exchangeJobState.query,
      exchangeJobState.filename,
      exchangeJobState.fileType,
      exchangeJobState.delimiter,
      exchangeJobState.customDelimeter,
      exchangeJobState.fieldEnclosedBy,
      exchangeJobState.encoding,
      exchangeJobState.hasHeaderRow,
      exchangeJobState.schemaName,
      exchangeJobState.tableName,
      exchangeJobState.destinationAction,
      exchangeJobState.existingTableAction,
      exchangeJobState.fields,
      exchangeJobState.bucket,
      exchangeJobState?.resetFiles,
      exchangeJobState.compression,
      exchangeJobState.singleFile,
      exchangeJobState.maxFileSize,
      exchangeJobState.externalIdFieldName,
      exchangeJobState.nullValue,
      exchangeJobState.customNullValue,
      exchangeJobState.onError,
      exchangeJobState.onErrorNumber,
      exchangeJobState.conversionFrom,
      exchangeJobState.renameFileRegex,
      exchangeJobState.unzipFiles,
      exchangeJobState.regexMatchPattern,
      exchangeJobState.regexReplacePattern,
      exchangeJobState.sourceType,
      exchangeJobState.docuList,
      nameState.name,
      nameState?.description,
      isExternalSnowflake,
      runTimeState.cluster,
      runTimeState.runJobAsUserId,
      runTimeState.runJobAsUserFullName,
      destinationType,
      scheduleState.scheduleEnabled,
      inCreateMode,
      editJob.position,
      editJob.id,
      defaultDelimiter,
      defaultFieldEnclosedBy,
      defaultEncoding,
      Workspace_id,
      defaultCluster,
      defaultNullValue,
    ]
  );

  // to get config for save based on location for mysql, external snowflake and current workspace
  const getLocationConfig = useCallback(() => {
    // if location type is mysql
    if (
      (transformJobState?.locationList.find(({ value }) => value === transformJobState.location)?.type || "") !==
      "snowflake"
    )
      return {};

    const baseConfig = {
      cluster: runTimeState.cluster,
      database: transformJobState.database,
      schema: transformJobState.schema,
    };

    // if location type is external snowflake
    if (transformJobState.location !== "SNOWFLAKE") return baseConfig;

    const userConfig =
      accessRole === 1
        ? {
            runJobAsUserId: runTimeState.runJobAsUserId,
            runJobAsUserFullName: runTimeState.runJobAsUserFullName,
          }
        : {
            ...(compareQuery(transformTabRef.current.query, transformJobState.query)
              ? {
                  runJobAsUserId: user.User_id,
                  runJobAsUserFullName: currentUserFullName,
                }
              : {
                  runJobAsUserId: runTimeState.runJobAsUserId,
                  runJobAsUserFullName: runTimeState.runJobAsUserFullName,
                }),
          };

    // if location type is current workspace snowflake
    return { ...baseConfig, ...userConfig };
  }, [
    accessRole,
    compareQuery,
    currentUserFullName,
    runTimeState.cluster,
    runTimeState.runJobAsUserFullName,
    runTimeState.runJobAsUserId,
    transformJobState.database,
    transformJobState.location,
    transformJobState?.locationList,
    transformJobState.query,
    transformJobState.schema,
    user.User_id,
  ]);

  const handleDataTransformSave = useCallback(
    async (DataPipeline_id) => {
      const wsService = new WorkspaceService();

      // If popup is open then just close it first
      if (jobReassignment.openConfirm) setPrevState(setJobReassignment, { openConfirm: false });

      let response = null;
      const submitData = {
        name: nameState.name.trim(),
        description: nameState?.description?.trim() || "",
        Type: transformJobState.Type,
        location: transformJobState.location,
        DataPipeline_id: DataPipeline_id,
        query: transformJobState.query,
        schedule: cronScheduleRef.current || DETAUL_CRON_VALUE,
        scheduleEnabled: scheduleState.scheduleEnabled || false,
        cluster: runTimeState.cluster,
        database: transformJobState.database,
        schema: transformJobState.schema,
        fromEditPanel: true,
        ...(inCreateMode ? { position: editJob.position } : {}),
        ...getLocationConfig(),
        queryLastUpdatedByName: inCreateMode
          ? currentUserFullName
          : compareQuery(transformTabRef.current.query, transformJobState.query)
          ? currentUserFullName
          : transformJobState.queryLastUpdatedByName,
      };

      if (inCreateMode) response = await wsService.createTransformJob(submitData, Workspace_id);
      else response = await wsService.updateTransformJob(submitData, Workspace_id, editJob.id);

      return response;
    },
    [
      jobReassignment.openConfirm,
      nameState.name,
      nameState?.description,
      transformJobState.Type,
      transformJobState.location,
      transformJobState.query,
      transformJobState.database,
      transformJobState.schema,
      transformJobState.queryLastUpdatedByName,
      scheduleState.scheduleEnabled,
      runTimeState.cluster,
      inCreateMode,
      editJob.position,
      editJob.id,
      getLocationConfig,
      currentUserFullName,
      compareQuery,
      Workspace_id,
    ]
  );

  const handleRoutingSave = useCallback(
    async (DataPipeline_id) => {
      const wsService = new WorkspaceService();

      // If popup is open then just close it first
      if (jobReassignment.openConfirm) setPrevState(setJobReassignment, { openConfirm: false });

      let response = null;
      const submitData = {
        name: nameState.name.trim(),
        description: nameState?.description?.trim() || "",
        Type: transformJobState.Type,
        location: transformJobState.location,
        DataPipeline_id: DataPipeline_id,
        query: transformJobState.query,
        schedule: cronScheduleRef.current || DETAUL_CRON_VALUE,
        scheduleEnabled: scheduleState.scheduleEnabled || false,
        cluster: runTimeState.cluster,
        database: transformJobState.database,
        schema: transformJobState.schema,
        fromEditPanel: true,
        ...(inCreateMode ? { position: editJob.position } : {}),
        ...getLocationConfig(),
        queryLastUpdatedByName: inCreateMode
          ? currentUserFullName
          : compareQuery(transformTabRef.current.query, transformJobState.query)
          ? currentUserFullName
          : transformJobState.queryLastUpdatedByName,
      };

      if (inCreateMode) response = await wsService.createRoutingJob(submitData, Workspace_id);
      else response = await wsService.updateRoutingJob(submitData, Workspace_id, editJob.id);

      return response;
    },
    [
      jobReassignment.openConfirm,
      nameState.name,
      nameState?.description,
      transformJobState.Type,
      transformJobState.location,
      transformJobState.query,
      transformJobState.database,
      transformJobState.schema,
      transformJobState.queryLastUpdatedByName,
      scheduleState.scheduleEnabled,
      runTimeState.cluster,
      inCreateMode,
      editJob.position,
      editJob.id,
      getLocationConfig,
      currentUserFullName,
      compareQuery,
      Workspace_id,
    ]
  );

  const handleNotebookSave = useCallback(
    async (DataPipeline_id) => {
      const wsService = new WorkspaceService();

      // If popup is open then just close it first
      if (jobReassignment.openConfirm) setPrevState(setJobReassignment, { openConfirm: false });

      let response = null;
      const submitData = {
        name: nameState.name.trim(),
        description: nameState?.description?.trim() || "",
        type: notebookJobState.type,
        jobPipeline: notebookJobState.jobPipeline,
        DataPipeline_id: DataPipeline_id,
        schedule: cronScheduleRef.current || DETAUL_CRON_VALUE,
        scheduleEnabled: scheduleState.scheduleEnabled || false,
        fromEditPanel: true,
        ...(inCreateMode ? { position: editJob.position } : {}),
      };

      if (inCreateMode) response = await wsService.createNotebookJob(submitData, Workspace_id);
      else response = await wsService.updateNotebookJob(submitData, Workspace_id, editJob.id);

      return response;
    },
    [
      Workspace_id,
      editJob.id,
      editJob.position,
      inCreateMode,
      jobReassignment.openConfirm,
      nameState?.description,
      nameState.name,
      notebookJobState.jobPipeline,
      notebookJobState.type,
      scheduleState.scheduleEnabled,
    ]
  );

  const handleNotificationSave = useCallback(
    async (DataPipeline_id) => {
      const wsService = new WorkspaceService();

      // If popup is open then just close it first
      if (jobReassignment.openConfirm) setPrevState(setJobReassignment, { openConfirm: false });

      let response = null;

      const sections = additionalDataSections.map(
        ({ key, query, runJobAsUserId, runJobAsUserFullName, ...other }, index) => {
          const queryComparision = compareQuery(additionalDataSectionsRef[key]?.query ?? "", query);
          const shouldUseCurrentUser = accessRole !== 1 && queryComparision;
          const userConfig = {
            runJobAsUserId: shouldUseCurrentUser ? user.User_id : runJobAsUserId,
            runJobAsUserFullName: shouldUseCurrentUser ? currentUserFullName : runJobAsUserFullName,
          };

          return {
            ...other,
            key,
            query,
            runJobAsUserId,
            runJobAsUserFullName,
            queryResultAs: other.queryResultAs ?? 1,
            Cluster: other.Cluster ?? defaultCluster,
            ...(other.location === "SNOWFLAKE" ? userConfig : {}),
            queryLastUpdatedByName:
              inCreateMode || queryComparision ? currentUserFullName : other.queryLastUpdatedByName,
            fileName: other.fileName || `EXPORTED_DATA_${index + 1}`,
          };
        }
      );

      const submitData = {
        fromEditPanel: true,
        DataPipeline_id,

        name: nameState.name.trim(),
        description: nameState?.description?.trim() || "",
        ...(inCreateMode ? { position: editJob.position } : {}),

        schedule: cronScheduleRef.current || DETAUL_CRON_VALUE,
        scheduleEnabled: scheduleState.scheduleEnabled || false,
        cluster: runTimeState.cluster,

        Type: notificationState.Type,
        location: notificationState.location,

        subject: notificationState.subject,
        body: await base64Encode(notificationState.body ?? ""),
        emails: notificationState.emails,

        additionalDataConfig: sections,
      };

      if (inCreateMode) response = await wsService.createNotificationJob(submitData, Workspace_id);
      else response = await wsService.updateNotificationJob(submitData, Workspace_id, editJob.id);

      return response;
    },
    [
      jobReassignment.openConfirm,
      additionalDataSections,
      nameState.name,
      nameState?.description,
      inCreateMode,
      editJob.position,
      editJob.id,
      scheduleState.scheduleEnabled,
      runTimeState.cluster,
      notificationState.Type,
      notificationState.location,
      notificationState.subject,
      notificationState.body,
      notificationState.emails,
      Workspace_id,
      compareQuery,
      accessRole,
      user.User_id,
      currentUserFullName,
      defaultCluster,
    ]
  );

  //Validating job is configured or not
  const isJobConfigured = useCallback(async () => {
    //Validating Exchange job
    if (
      jobType === 2 &&
      ["snowflake", "mysql"].includes(destinationType?.toLowerCase()) &&
      !exchangeJobState.isFieldsConfigured
    ) {
      const {
        destination,
        delimiter,
        hasHeaderRow,
        encoding,
        fieldEnclosedBy,
        schemaName,
        tableName,
        destinationAction,
        existingTableAction,
        source,
        groupBy,
      } = previousState;
      if (
        destination !== exchangeJobState.destination ||
        !(delimiter === exchangeJobState.delimiter || delimiter === exchangeJobState.customDelimeter) ||
        hasHeaderRow !== exchangeJobState.hasHeaderRow ||
        encoding !== exchangeJobState.encoding ||
        fieldEnclosedBy !== exchangeJobState.fieldEnclosedBy ||
        schemaName !== exchangeJobState.schemaName ||
        tableName !== exchangeJobState.tableName ||
        destinationAction !== exchangeJobState.destinationAction ||
        existingTableAction !== exchangeJobState.existingTableAction ||
        source !== exchangeJobState.source ||
        groupBy !== exchangeJobState.groupBy
      ) {
        setPrevState(setNotAllowedDialog, {
          openConfirm: true,
          confirmMessage: (
            <span>
              <span>
                {!inCreateMode
                  ? "Source or destination file properties were updated, please save the new table configurations to update the job."
                  : "Please configure the table to save the job."}
              </span>
            </span>
          ),
          title: "Action blocked",
        });

        return false;
      }
    }

    return true;
  }, [
    exchangeJobState.customDelimeter,
    exchangeJobState.delimiter,
    exchangeJobState.destination,
    exchangeJobState.destinationAction,
    destinationType,
    exchangeJobState.encoding,
    exchangeJobState.existingTableAction,
    exchangeJobState.fieldEnclosedBy,
    exchangeJobState.hasHeaderRow,
    exchangeJobState.isFieldsConfigured,
    exchangeJobState.schemaName,
    exchangeJobState.groupBy,
    exchangeJobState.source,
    exchangeJobState.tableName,
    inCreateMode,
    jobType,
    previousState,
  ]);

  const checkForSchemaPermission = useCallback(
    async (database, schema, schemaList = schemas) => {
      let schemasToCheck = [];
      if (schemaList === null) schemasToCheck = await getSchemaList();
      else
        schemaList?.forEach(({ title: SCHEMA_NAME, groupBy }) => {
          if (!schemasToCheck[groupBy]) schemasToCheck[groupBy] = [];
          schemasToCheck[groupBy].push({ SCHEMA_NAME });
        });

      return schemasToCheck[database]?.some(({ SCHEMA_NAME }) => SCHEMA_NAME === schema);
    },
    [getSchemaList, schemas]
  );

  const handleSave = useCallback(
    async (closePanel = true) => {
      // Valiation will happen tab-wise
      const errors = await validate(
        {
          nameTab: nameState,
          dataObject: { IsDataVaultEnabled, dataSets },
          runtime: {
            ...runTimeState,
            isExternalSnowflake,
            isSnowflake:
              !isExternalSnowflake &&
              [exchangeJobState.sourceType?.toLowerCase(), destinationType]?.includes("snowflake"),
          },
          transformTab: {
            ...transformJobState,
            isSnowflake:
              transformJobState.location === "SNOWFLAKE" ||
              transformJobState.locationList.find(
                ({ value, type }) => value === transformJobState.location && type === "snowflake"
              ),
          },
          actionTab: { ...exchangeJobState, destinationType, isExternalSnowflake },
          targetTab: notebookJobState,
          queryTab: {
            query: dataQualityJobState.query,
            queryError: dataQualityJobState.queryError,
          },
          notificationTab: notificationState,
          additionalDataTab: additionalDataSections,
        },
        jobType
      );

      // If errors found then show the errors and do not save the job details
      if (errors.length > 0) {
        dispatch(showToast({ message: errors, type: "error" }));
        return { errorInJob: true, closePanel };
      }

      if (
        jobType === 2 &&
        ["snowflake", "mysql"]?.includes(destinationType) &&
        !!(exchangeJobState?.sourcePath && exchangeJobState?.sourcePath.split(",/").length > 1)
      ) {
        dispatch(showToast({ message: "Please select just 1 file to create table in the database", type: "error" }));
        return { errorInJob: true, closePanel };
      }

      // If it is a Data exchange Job then check if that job is configured or not, if not configured then show popup
      if (jobType === 2 && !(await isJobConfigured())) return { errorInJob: true, closePanel };

      if (
        // For Data transform and routing
        [3, 8].includes(jobType) &&
        transformJobState.location === "SNOWFLAKE" &&
        !(await checkForSchemaPermission(transformJobState.database, transformJobState.schema))
      ) {
        setSchemaError(true);
        setTransformJobState((prev) => ({ ...prev, database: "", schema: "" }));

        dispatch(
          showToast({ message: "Schema does not exists or you do not have access to work on it.", type: "error" })
        );
        return { errorInJob: true, closePanel };
      }

      if (jobType === 9) {
        for (const [index, section] of additionalDataSections.entries()) {
          if (
            section.location === "SNOWFLAKE" &&
            section.includeResult &&
            !(await checkForSchemaPermission(
              section.database,
              section.schema,
              locationSchemasRef.current[section.location]?.schemas
            ))
          ) {
            setPrevState(setVariableError, { [section.key]: { ...variableError[section.key], schemaError: true } });

            dispatch(
              showToast({
                message: `Section ${index + 1}: Schema does not exists or you do not have access to work on it!`,
                type: "error",
              })
            );

            setAdditionalDataKeys((s) =>
              s.map((item) => {
                if (item.key === section.key) return { ...item, database: "", schema: "" };
                return item;
              })
            );

            return { errorInJob: true, closePanel };
          }

          if (variableError[section.key]?.userNotExist) {
            dispatch(
              showToast({
                message: `Section ${index + 1}: Run the job as is required!`,
                type: "error",
              })
            );

            return { errorInJob: true, closePanel };
          }
        }
      }

      setLoading(true);

      let newDataPipeline = DataPipeline_id;
      if (!DataPipeline_id) {
        const response = await canvasRef.current.addNewDataPipeline(true);

        if (!response.success) {
          setLoading(false);
          return { errorInJob: true, closePanel };
        }
        newDataPipeline = response.data.DataPipeline_id;
      }

      const pipelineID = nameState.DataPipeline_id === -1 ? newDataPipeline : nameState.DataPipeline_id;

      let response = {};
      if (DATA_MODELING_JOBS.includes(jobType)) response = await handleDataModelingSave(pipelineID);
      else if (jobType === 2) response = await handleDataExchangeSave(pipelineID);
      else if (jobType === 3) response = await handleDataTransformSave(pipelineID);
      else if (jobType === 8) response = await handleRoutingSave(pipelineID);
      else if (jobType === 7) response = await handleNotebookSave(pipelineID);
      else if (jobType === 9) response = await handleNotificationSave(pipelineID);

      if (response?.success) {
        let updateData = {};
        updateData = {
          ...(inCreateMode ? { prevId: editJob.id, position: editJob.position } : {}),
          name: nameState.name,
          JobPipelineId: nameState.DataPipeline_id,
          Job_id: inCreateMode ? response.data.Job_id : editJob.id,
        };

        if (!closePanel && !inCreateMode && nameState.DataPipeline_id.toString() !== DataPipeline_id.toString())
          closePanel = true;

        if (closePanel) {
          dispatch(handleEditJob(null));
          if (!DataPipeline_id) {
            dispatch(handlePipelineList(false));
            navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}/data-pipeline/${newDataPipeline}`);
          }
        } else {
          setIsChanged({
            Name: false,
            DataSets: false,
            Schedule: false,
            Runtime: false,
            Notebook: false,
          });

          //Resetting runtime tab ref
          if (DATA_MODELING_JOBS.includes(jobType)) runTimeTabRef.current = { cluster: runTimeState.cluster };
          else if (jobType === 3) runTimeTabRef.current = { ...runTimeTabRef.current, cluster: runTimeState.cluster };
        }

        dispatch(
          showToast({
            message: response.message,
            type: "success",
          })
        );

        if (DataPipeline_id) canvasRef.current.updateCanvas(inCreateMode ? "Add" : "Update", updateData);
      } else if (!DataPipeline_id) {
        const dataPipelineService = new DataPipelineService();
        await dataPipelineService.deleteDataPipeline(Workspace_id, newDataPipeline);
      }

      setLoading(false);

      return { errorInJob: !response?.success, closePanel };
    },
    [
      nameState,
      IsDataVaultEnabled,
      dataSets,
      runTimeState,
      isExternalSnowflake,
      exchangeJobState,
      destinationType,
      transformJobState,
      notebookJobState,
      dataQualityJobState.query,
      dataQualityJobState.queryError,
      notificationState,
      additionalDataSections,
      jobType,
      isJobConfigured,
      checkForSchemaPermission,
      setLoading,
      DataPipeline_id,
      handleDataModelingSave,
      handleDataExchangeSave,
      handleDataTransformSave,
      handleRoutingSave,
      handleNotebookSave,
      handleNotificationSave,
      dispatch,
      variableError,
      canvasRef,
      inCreateMode,
      editJob.id,
      editJob.position,
      navigate,
      Workspace_id,
    ]
  );

  const getWorkspaceParticipants = useCallback(async () => {
    const wsService = new WorkspaceService();
    const { success, data } = await wsService.getWorkspaceParticipants(Workspace_id, [1, 2]);
    if (success) participantsExcludeVisitor.current = data;
  }, [Workspace_id]);

  const getLocationList = useCallback(
    async (fromAddBtn = false, onlyLocations = []) => {
      if (fromAddBtn) setLoading(true);
      const wsService = new WorkspaceService();
      const locations = await wsService.getLocationList(Workspace_id);

      let filteredLocations =
        locations?.filter(({ Detail }) => {
          if (onlyLocations.length !== 0) return onlyLocations.includes(Detail?.type?.toLowerCase());
          return true;
        }) || [];

      if (onlyLocations.length) {
        filteredLocations = filteredLocations?.map(({ Stage_id, Name, Detail }) => ({
          value: Stage_id,
          text: Name,
          type: Detail?.type || "",
        }));
      }

      const locationList = {
        locationList: [
          ...(onlyLocations.length !== 0
            ? [{ value: "SNOWFLAKE", text: workspace.workspace.Name + " (Current Workspace DB)", type: "snowflake" }]
            : [
                {
                  Stage_id: "SNOWFLAKE",
                  Name: workspace.workspace.Name + " (Current Workspace DB)",
                  Detail: { type: "snowflake" },
                },
              ]),
          ...filteredLocations,
        ],
      };

      if (fromAddBtn) setLoading(false);
      return locationList;
    },
    [Workspace_id, setLoading, workspace.workspace.Name]
  );

  const loadLocationList = useCallback(async () => {
    const locationList = await getLocationList(true);
    setPrevState(setExchangeJobState, { ...locationList });
  }, [getLocationList, setExchangeJobState]);

  const handleJobPurposeChange = useCallback(
    (e) => {
      setExchangeJobState((prev) => {
        return {
          ...prev,
          jobPurpose: e.target.value,
          destination: "",
          delimiter: defaultDelimiter,
          hasHeaderRow: true,
          encoding: defaultEncoding,
          fieldEnclosedBy: defaultFieldEnclosedBy,
          fields: [],
          schemaName: "",
          groupBy: "",
          tableName: "",
          destinationAction: "new",
          existingTableAction: "overwrite",
          source: "",
          sourcePath: "",
          nullValue: defaultNullValue,
          conversionFrom: CONVERSION_FROM_SAS,
          container: "",
          desinationContainer: "",
          sourceType: "",
          customDelimeter: "",
          fileType: defaultFileType,
          compression: defaultCompression,
          onError: defaultErrorAction,
          externalIdFieldName: "",
        };
      });
    },
    [
      defaultCompression,
      defaultDelimiter,
      defaultEncoding,
      defaultErrorAction,
      defaultFieldEnclosedBy,
      defaultFileType,
      defaultNullValue,
    ]
  );

  const loadSnowflakeSchemas = useCallback(async () => {
    const wsService = new WorkspaceService();
    const { data, success } = await wsService.getJobsSchemas(Workspace_id);
    if (!success) return navigate(`/my-workspaces/${Workspace_id}`);
    const returnData = { snowflakeSchemas: [], allSchemas: [] };

    if (success) {
      returnData.snowflakeSchemas = data.schemasWithReadAndWrite;
      returnData.allSchemas = data.allSchemas;
    }
    return returnData;
  }, [Workspace_id, navigate]);

  const loadmysqlSchemas = useCallback(
    async (Stage_id) => {
      const mysqlService = new SQLService();
      const response = await mysqlService.getMysqlSchemas(Workspace_id, Stage_id);
      if (response === "Unauthorized") navigate(`/my-workspaces/${Workspace_id}`);
      let allSchemas = [];
      if (response.success) allSchemas = response.data;
      return allSchemas;
    },
    [Workspace_id, navigate]
  );

  const loadSalesforceObjects = useCallback(
    async (Stage_id) => {
      if (!Stage_id) return [];
      const salesforce = new Salesforce();
      const response = await salesforce.getObjects(Workspace_id, Stage_id);
      if (response.success)
        return {
          tableList: response.data.objects.map(({ value }) => value),
          ...(inCreateMode ? { schemaName: "update" } : {}),
        };
      return { tableList: [], ...(inCreateMode ? { schemaName: "update" } : {}) };
    },
    [Workspace_id, inCreateMode]
  );

  const loadVeevaObjects = useCallback(
    async (Stage_id) => {
      if (!Stage_id) return [];
      const veevavault = new Veevavault();
      const response = await veevavault.getVeevaObjects(Workspace_id, Stage_id);

      if (response.success)
        return {
          tableList: response.data.objects.map(({ value }) => value),
          ...(inCreateMode ? { schemaName: "update" } : {}),
        };
      return { tableList: [], ...(inCreateMode ? { schemaName: "update" } : {}) };
    },
    [Workspace_id, inCreateMode]
  );

  const getExternalSnowflakeCluster = useCallback(
    async (location) => {
      if (!location || location === "SNOWFLAKE") return [];

      if (jobType === 9) {
        const avaialbleClusters = locationClustersRef.current[location];
        if (!!avaialbleClusters) return avaialbleClusters;
      }

      const wsService = new WorkspaceService();
      const { success, data } = await wsService.getExternalSnowflakeCluster(Workspace_id, location);
      if (success && data?.length) {
        const clusters = data?.map((cluster) => ({ value: cluster?.name || "" }));
        setExtSnowflakeClusterList(clusters);
        if (jobType === 9) locationClustersRef.current[location] = clusters;
        return clusters;
      }
      setExtSnowflakeClusterList([]);
      if (jobType === 9) locationClustersRef.current[location] = [];
      return [];
    },
    [Workspace_id, jobType]
  );

  const loadExternalSnowflakeSchemas = useCallback(
    async (location = false, orignalResponse = false) => {
      let returnData = { externalSnowflakeSchemas: [] };
      if (!location) return returnData;
      const wsService = new WorkspaceService();
      const { success, data } = await wsService.getExternalSnowflakeSchemas(Workspace_id, location, {
        schemasOnly: true,
      });
      if (success) {
        const externalSnowflakeSchemas = arrangeSchema(data);
        setPrevState(setExchangeJobState, { externalSnowflakeSchemas });
        returnData = { externalSnowflakeSchemas, ...(orignalResponse ? { data } : {}) };
      }
      return returnData;
    },
    [Workspace_id]
  );

  useEffect(() => {
    getExternalSnowflakeCluster();

    return () => {};
  }, [Workspace_id, getExternalSnowflakeCluster]);

  const loadSchemasClusters = useCallback(
    async (locationType, location, fromSource = false) => {
      if (fromSource && locationType !== "snowflake") return;
      else if (
        ["", "s3", "sftp", "azure"].includes(locationType) ||
        (["mysql", "salesforce", "veevavault"].includes(locationType) && !location)
      )
        return;

      setPrevState(setExchangeJobState, { loading: true });
      let data = {};
      if (locationType === "mysql") data.allSchemas = (await loadmysqlSchemas(location)) || [];
      else if (locationType === "snowflake") {
        if (location === "SNOWFLAKE") data = await loadSnowflakeSchemas();
        else {
          const [schemaData, clusterList] = await Promise.all([
            loadExternalSnowflakeSchemas(location),
            getExternalSnowflakeCluster(location),
          ]);

          data = schemaData;
          // Check if the cluster exists (do not display error as connection changed)
          const isClusterExist = checkIsClusterExist(clusterList, runTimeState?.cluster);
          setClusterError(false);
          setExtSnowflakeClusterList(clusterList);
          setRunTimeState({ cluster: isClusterExist ? runTimeState?.cluster : "" });
        }
      } else if (locationType === "salesforce")
        data = await loadSalesforceObjects(location); //here "update" is default selection for operation type
      else if (locationType === "veevavault") data = await loadVeevaObjects(location); //here "update" is default selection for operation type

      setPrevState(setExchangeJobState, {
        ...data,
        loading: false,
        loadingDropBoxValues: false,
      });
    },
    [
      getExternalSnowflakeCluster,
      loadExternalSnowflakeSchemas,
      loadSalesforceObjects,
      loadVeevaObjects,
      loadSnowflakeSchemas,
      loadmysqlSchemas,
      runTimeState?.cluster,
    ]
  );

  const generateName = useCallback(() => {
    const makeVar = (count) => `$ADDITIONAL_DATA_${count}$`;
    const makeFile = (count) => `Exported_Data_${count}`;

    let varIndex = variableNames.length + 1;
    let newVariableName = makeVar(varIndex);
    while (variableNames.includes(newVariableName.toLowerCase())) {
      varIndex += 1;
      newVariableName = makeVar(varIndex);
    }

    let fileIndex = fileNames.length + 1;
    let newFileName = makeFile(fileIndex);
    while (fileNames.includes(newFileName.toLowerCase())) {
      fileIndex += 1;
      newFileName = makeFile(fileIndex);
    }

    const key = getUUID();
    setAdditionalDataKeys((prev) => [
      ...prev,
      {
        key,
        ...addNewSection,
        variable: newVariableName,
        fileName: newFileName,
      },
    ]);
  }, [addNewSection, fileNames, variableNames]);

  // For fetching data for Data Exchange Jobs
  useEffect(() => {
    if (Number(editJob.data.type) === 2) {
      //Get Location List for "Connection" Dropdown

      const getDataExchangeSchedule = async () => {
        const wsService = new WorkspaceService();

        const { success, data } = await wsService.getDataExchangeSchedule(Workspace_id, editJob.id);
        if (!success) return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);

        const { Job_id, status, Type, Name, StartTime, averageRunTimeInMS } = data;
        if (status === "running" || status === "terminating") setValue("Runtime");

        dispatch(
          saveJobDetails({
            Job_id,
            status,
            Type,
            Name,
            StartTime,
            averageRunTimeInMS,
          })
        );

        cronScheduleRef.current = data.Schedule;

        if (data.status === "draft") {
          return {
            jobName: data.Name,
            timeZone: data.ServerTimeZone,
            scheduleEnabled: Boolean(data.Enabled),
            nextRunTime: data.nextRun,
            DataPipeline_id: data.DataPipeline_id,
          };
        } else {
          const destinationType = data.Detail?.destinationType?.toLowerCase() || "snowflake";
          const sourceType = data.Detail?.sourceType?.toLowerCase() || "snowflake";

          const sourcePath =
            data.Detail.dest_stage_id?.toString()?.toLowerCase() === "snowflake" ||
            ["mysql", "salesforce", "snowflake", "veevavault"].includes(destinationType)
              ? data.Detail.path
              : data?.Detail?.files?.join(",");
          const destinationPath = data.Detail.location;

          const _delimiters =
            ["salesforce", "veevavault"].includes(destinationType) ||
            data.Detail.sourceType.toLowerCase() === "salesforce"
              ? salesforceDelimiters
              : delimiters;

          /* Added logic for custom option selection in Delimiters dropdown */
          const formatDelimiter = { delimiter: data.Detail.delimiter, customDelimeter: "" };
          if (formatDelimiter.delimiter && !_delimiters.some((obj) => obj.value === data.Detail.delimiter)) {
            formatDelimiter.delimiter = "custom";
            formatDelimiter.customDelimeter = data.Detail.delimiter;
          }

          const formatNullValue = { nullValue: data.Detail.nullValue, customNullValue: "" };
          if (formatNullValue.nullValue && !nullValues.some((obj) => obj.value === data.Detail.nullValue)) {
            formatNullValue.nullValue = "custom";
            formatNullValue.customNullValue = data.Detail.nullValue;
          }

          const isQuickImportJob =
            (["s3", "azure", "sftp", "sharepoint"].includes(sourceType) &&
              destinationType?.toLowerCase() === "snowflake") ||
            (sourceType === "snowflake" && destinationType === "s3") ||
            ["mysql", "salesforce", "snowflake", "veevavault"].includes(destinationType);

          let allSchemas = [];
          let snowflakeSchemas = [];
          let externalSnowflakeSchemas = [];
          let schemasByDB = {};
          let tableList = [];
          let isSchemaExist = true;
          let haveSchemaPermission = true;
          let isClusterExist = true;

          // This entire If block focuses on what if schema/table is deleted or it is removed from access
          if (isQuickImportJob) {
            if (destinationType === "mysql") allSchemas = (await loadmysqlSchemas(data.Detail?.dest_stage_id)) || [];
            else if (destinationType === "salesforce") {
              const response = (await loadSalesforceObjects(data.Detail?.dest_stage_id)) || [];
              tableList = response.tableList;

              if (!data.Detail.schemaName) data.Detail.schemaName = "update";
            } else if (destinationType === "veevavault") {
              const response = (await loadVeevaObjects(data.Detail?.dest_stage_id)) || [];
              tableList = response.tableList;

              if (!data.Detail.schemaName) data.Detail.schemaName = "update";
            } else if (data.Detail.dest_stage_id === "SNOWFLAKE") {
              const snowflakeResponse = await loadSnowflakeSchemas();
              snowflakeSchemas = snowflakeResponse?.snowflakeSchemas;
              allSchemas = snowflakeResponse?.allSchemas;
            } else if (
              (sourceType === "snowflake" && data?.Detail?.source_stage_id !== "SNOWFLAKE") ||
              (destinationType === "snowflake" && data?.Detail?.dest_stage_id !== "SNOWFLAKE")
            ) {
              const [schemaData, clusterList] = await Promise.all([
                loadExternalSnowflakeSchemas(
                  sourceType === "snowflake" ? data.Detail.source_stage_id : data.Detail.dest_stage_id,
                  true
                ),
                getExternalSnowflakeCluster(
                  sourceType === "snowflake" ? data.Detail.source_stage_id : data.Detail.dest_stage_id
                ),
              ]);

              // Destructure the result from schemaData
              const { externalSnowflakeSchemas: schemas, data: dataFromSchema } = schemaData;

              // Update state with the fetched data
              externalSnowflakeSchemas = [...schemas];
              schemasByDB = dataFromSchema;

              // Check if the cluster exists
              isClusterExist = checkIsClusterExist(clusterList, data?.Detail?.Cluster);

              // Update runtime state
              setClusterError(!isClusterExist);
              setExtSnowflakeClusterList(clusterList);
            }
          }

          if (["salesforce", "veevavault"].includes(destinationType)) isSchemaExist = true;
          else if ([sourceType, destinationType]?.includes("snowflake") || destinationType === "mysql") {
            if (
              data.Detail.dest_stage_id === "SNOWFLAKE" ||
              data.Detail.source_stage_id === "SNOWFLAKE" ||
              destinationType === "mysql"
            ) {
              isSchemaExist = allSchemas.some(
                (schema) => schema.toLowerCase() === data?.Detail?.schemaName?.toLowerCase()
              );

              if (destinationType !== "mysql") {
                haveSchemaPermission = snowflakeSchemas.some(
                  (schema) => schema.toLowerCase() === data.Detail.schemaName.toLowerCase()
                );
                if (!haveSchemaPermission) {
                  restrictedObject.current = {
                    schema: data.Detail.schemaName,
                    table: data.Detail.tableName,
                  };
                }
              }
            } else isSchemaExist = checkIsSchemaExist(schemasByDB, data?.Detail?.database, data?.Detail?.schemaName);
          } else isSchemaExist = false;

          // Flag for rename file
          const renameFileRegex = Boolean(data?.Detail?.regexMatchPattern && data?.Detail?.regexReplacePattern);

          if (isQuickImportJob) {
            setPreviousState((prev) => ({
              ...prev,
              destination: data.Detail.dest_stage_id,
              allSchemas,
              snowflakeSchemas,
              source: data.Detail.source_stage_id,
              sourcePath: sourcePath && sourcePath[0] !== "/" ? "/" + sourcePath : sourcePath,
              fields: data.Detail.fields,
              schemaName: isSchemaExist ? data.Detail.schemaName || "" : "",
              groupBy: isSchemaExist ? data.Detail.database || "" : "",
              tableName: isSchemaExist ? data.Detail.tableName || "" : "",
              destinationAction: isSchemaExist && data.Detail.tableName ? "existing" : "new",
              existingTableAction: isSchemaExist && data.Detail.append ? "append" : "overwrite",
              nullValue: data.Detail.nullValue || defaultNullValue,
              unzipFiles: data.Detail.unzipFiles || false,
              renameFileRegex,
              regexMatchPattern: data?.Detail?.regexMatchPattern || "",
              regexReplacePattern: data?.Detail?.regexReplacePattern || "",
              ...(data.Detail.filetype === "csv"
                ? {
                    delimiter: data.Detail.delimiter,
                    hasHeaderRow: data.Detail.hasHeaderRow,
                    encoding: data.Detail.encoding,
                    fieldEnclosedBy: data.Detail.fieldEnclosedBy,
                  }
                : {
                    delimiter: defaultDelimiter,
                    fieldEnclosedBy: defaultFieldEnclosedBy,
                    encoding: defaultEncoding,
                    hasHeaderRow: true,
                  }),
              ...(data.Detail.sourceType.toLowerCase() === "snowflake" ||
              data.Detail.sourceType?.toLocaleLowerCase() === "mysql"
                ? { fieldEnclosedBy: data.Detail.fieldEnclosedBy }
                : {}),
            }));
          }

          return {
            jobPurpose: data?.Detail?.jobPurpose || JOB_PURPOSE_EXCHANGE,
            jobName: data.Name,
            description: data.Description || "",
            source: data.Detail.source_stage_id || "",
            DataPipeline_id: data.DataPipeline_id,
            sourcePath: (sourcePath && sourcePath[0] !== "/" ? "/" + sourcePath : sourcePath) || "",
            resetFiles: data.Detail?.resetFiles ? data.Detail.resetFiles : false,
            query: data.Detail.query || "",
            filename: data?.Detail?.filename || "",
            fileType: data.Detail?.filetype || defaultFileType,
            customDelimeter: formatDelimiter.customDelimeter || "",
            delimiter: formatDelimiter?.delimiter || defaultDelimiter,
            customNullValue: formatNullValue.customNullValue || "",
            nullValue: formatNullValue?.nullValue || defaultNullValue,
            destination: data?.Detail?.dest_stage_id || "",
            allSchemas,
            snowflakeSchemas,
            externalSnowflakeSchemas,
            targetPath: destinationPath && destinationPath[0] !== "/" ? "/" + destinationPath : destinationPath,
            timeZone: data.ServerTimeZone,
            scheduleEnabled: Boolean(data.Enabled),
            nextRunTime: data.nextRun,
            sourceType: data.Detail.sourceType || "",
            bucket: data.Detail?.bucket || "",
            docuList: data.Detail?.docuList || "",
            runJobAsUserId: data.Detail?.runJobAsUserId || user.User_id,
            runJobAsUserFullName: data.Detail?.runJobAsUserFullName || currentUserFullName,
            hasHeaderRow: data.Detail.hasHeaderRow === undefined ? true : data.Detail.hasHeaderRow,
            compression: data.Detail.compression || defaultCompression,
            singleFile: data.Detail.singleFile || false,
            maxFileSize: parseInt(data.Detail.maxFileSize || DEFAULT_MAX_FILE_SIZE),
            externalIdFieldName: data.Detail.externalIdFieldName || "",
            renameFileRegex,
            unzipFiles: data.Detail.unzipFiles || false,
            regexMatchPattern: data?.Detail?.regexMatchPattern || "",
            regexReplacePattern: data?.Detail?.regexReplacePattern || "",
            ...(data?.Detail?.jobPurpose === JOB_PURPOSE_CONVERSION
              ? {
                  conversionFrom: data.Detail.conversionFrom || CONVERSION_FROM_SAS,
                }
              : {}),
            ...(isQuickImportJob
              ? {
                  encoding: data.Detail.encoding || defaultEncoding,
                  fields: data.Detail.fields || [],
                  schemaName: isSchemaExist ? data.Detail.schemaName || "" : "",
                  groupBy: isSchemaExist ? data.Detail.database || "" : "",
                  tableName: isSchemaExist ? data.Detail.tableName || "" : "",
                  tableList,
                  showSchemaError: !isSchemaExist,
                  destinationAction: isSchemaExist && data.Detail.tableName ? "existing" : "new",
                  existingTableAction: isSchemaExist && data.Detail.append ? "append" : "overwrite",
                  onError: data.Detail.onError || defaultErrorAction,
                  onErrorNumber: data.Detail.onErrorNumber || 0,
                }
              : {}),
            ...(isQuickImportJob ||
            sourceType === "snowflake" ||
            ["mysql", "salesforce", "veevavault"].includes(sourceType)
              ? { fieldEnclosedBy: data.Detail.fieldEnclosedBy || defaultFieldEnclosedBy }
              : {}),
            ...([sourceType, destinationType]?.includes("snowflake") && data.Detail.dest_stage_id !== "SNOWFLAKE"
              ? { Cluster: isClusterExist ? data?.Detail?.Cluster || "" : "" }
              : {}),
          };
        }
      };

      (async () => {
        let initialState = {};
        setLoading(true);

        initialState = {
          ...(await getLocationList()),
        };

        if (!inCreateMode)
          initialState = {
            ...initialState,
            ...(await getDataExchangeSchedule()),
          };

        if (!initialState.runJobAsUserId) {
          initialState.runJobAsUserId = user.User_id;
          initialState.runJobAsUserFullName = currentUserFullName;
        }

        await getWorkspaceParticipants();

        if (initialState.sourceType && initialState.sourceType.toLowerCase() === "azure")
          initialState.container = initialState.locationList.find(
            (loc) => loc.Stage_id === initialState.source
          )?.Detail.container;

        const desinationConnection = initialState.locationList.find((loc) => loc.Stage_id === initialState.destination);

        if (desinationConnection) initialState.desinationContainer = desinationConnection?.Detail?.container || "";

        if (!inCreateMode) {
          setPrevState(setNameState, {
            name: initialState.jobName,
            description: initialState.description || "",
            DataPipeline_id: initialState.DataPipeline_id,
          });
          nameTabRef.current = {
            name: initialState.jobName,
            description: initialState.description || "",
            DataPipeline_id: initialState.DataPipeline_id,
          };

          runTimeTabRef.current = {
            runJobAsUserId: initialState.runJobAsUserId,
          };

          if (
            !inCreateMode &&
            initialState.runJobAsUserId &&
            !participantsExcludeVisitor.current.some((user) => user.User_id === initialState.runJobAsUserId)
          ) {
            initialState.runJobAsUserId = null;
            initialState.runJobAsUserFullName = "";
          }

          setPrevState(setRunTimeState, {
            cluster: initialState.Cluster,
            runJobAsUserId: initialState.runJobAsUserId,
            runJobAsUserFullName: initialState.runJobAsUserFullName,
          });

          setPrevState(setScheduleState, {
            scheduleEnabled: initialState.scheduleEnabled || false,
            nextRunTime: initialState.nextRunTime || "",
            timeZone: initialState.timeZone || timezone,
          });
          scheduleTabRef.current = {
            scheduleEnabled: initialState.scheduleEnabled || false,
            schedule: cronScheduleRef.current,
          };
        }

        setExchangeJobState((prev) => {
          if (!inCreateMode) {
            actionTabRef.current = {
              jobPurpose: initialState.jobPurpose || prev.jobPurpose,
              sourceType: initialState.sourceType || prev.sourceType,
              bucket: initialState.bucket || prev.bucket,
              sourcePath: initialState.sourcePath || prev.sourcePath,
              source: initialState.source || prev.source,
              targetPath: initialState.targetPath || prev.targetPath,
              destination: initialState.destination || prev.destination,
              docuList: initialState.docuList || prev.docuList,
              delimiter: initialState.delimiter || prev.delimiter,
              customDelimeter: initialState.customDelimeter || prev.customDelimeter,
              nullValue: initialState.nullValue || prev.nullValue,
              customNullValue: initialState.customNullValue || prev.customNullValue,
              hasHeaderRow: initialState.hasHeaderRow || prev.hasHeaderRow,
              encoding: initialState.encoding || prev.encoding,
              fieldEnclosedBy: initialState.fieldEnclosedBy || prev.fieldEnclosedBy,
              groupBy: initialState.groupBy || prev.groupBy,
              schemaName: initialState.schemaName || prev.schemaName,
              tableName: initialState.tableName || prev.tableName,
              destinationAction: initialState.destinationAction || prev.destinationAction,
              existingTableAction: initialState.existingTableAction || prev.existingTableAction,
              fields: initialState.fields || prev.fields,
              query: initialState.query || prev.query,
              filename: initialState.filename || prev.filename,
              fileType: initialState.fileType || prev.fileType,
              resetFiles: initialState.resetFiles || prev.resetFiles,
              onError: initialState.onError || prev.onError,
              onErrorNumber: initialState.onErrorNumber || prev.onErrorNumber,
              conversionFrom: initialState.conversionFrom || prev.conversionFrom || CONVERSION_FROM_SAS,
              externalIdFieldName: initialState.externalIdFieldName || "",
              regexMatchPattern: initialState.regexMatchPattern || "",
              regexReplacePattern: initialState.regexReplacePattern || "",
            };
          }

          return {
            ...prev,
            ...initialState,
          };
        });

        setLoading(false);
      })();
    }
  }, [
    Workspace_id,
    currentUserFullName,
    defaultCompression,
    defaultDelimiter,
    defaultEncoding,
    defaultErrorAction,
    defaultFieldEnclosedBy,
    defaultFileType,
    defaultNullValue,
    delimiters,
    dispatch,
    editJob.data.type,
    editJob.id,
    getLocationList,
    getWorkspaceParticipants,
    inCreateMode,
    loadSnowflakeSchemas,
    loadmysqlSchemas,
    navigate,
    nullValues,
    salesforceDelimiters,
    setLoading,
    timezone,
    user.User_id,
    workspace.workspace.Name,
    getExternalSnowflakeCluster,
    loadExternalSnowflakeSchemas,
    loadSalesforceObjects,
    loadVeevaObjects,
  ]);

  // For fetching data for Data Transform Jobs
  useEffect(() => {
    const jobType = editJob.data.type;
    if (Number(jobType) === 3 || Number(jobType) === 8) {
      // Make this common for all jobs
      const getDataTransformSchedule = async () => {
        const wsService = new WorkspaceService();
        const { data, success } =
          jobType === 3
            ? await wsService.getDataTransformSchedule(Workspace_id, editJob.id)
            : await wsService.getRoutingSchedule(Workspace_id, editJob.id);
        if (!success) return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);

        const { Job_id, status, Type, Name, StartTime, averageRunTimeInMS } = data;
        if (status === "running" || status === "terminating") setValue("Runtime");

        dispatch(
          saveJobDetails({
            Job_id,
            status,
            Type,
            Name,
            StartTime,
            averageRunTimeInMS,
          })
        );

        cronScheduleRef.current = data.Schedule;

        return {
          name: data.Name,
          Type: data.Detail.Type || "SQL",
          location: data.Detail.location,
          DataPipeline_id: data.DataPipeline_id,
          description: data.Description || "",
          query: data.Detail.query,
          timeZone: data.ServerTimeZone,
          scheduleEnabled: Boolean(data.Enabled),
          nextRunTime: data.nextRun,
          runJobAsUserId: data.Detail.runJobAsUserId,
          runJobAsUserFullName: data.Detail.runJobAsUserFullName,
          queryLastUpdatedByName: data.Detail.queryLastUpdatedByName,
          database: data.Detail.database,
          schema: data.Detail.schema,
          cluster: data.Detail.Cluster || defaultCluster,
        };
      };

      async function fetchData() {
        setLoading(true);
        let initialState = { ...(await getLocationList(false, ["mysql", "snowflake"])) };
        let schemaFound = true;
        let clusterFound = true;

        if (!inCreateMode) {
          initialState = { ...initialState, ...(await getDataTransformSchedule()) };

          const location = initialState.location;
          const selectedLocation = initialState?.locationList?.find(({ value }) => value === location);
          const isLocationSnowflake = selectedLocation?.type === "snowflake";

          // To validate the schema exist or not
          if (isLocationSnowflake) {
            const schemaData = await getSchemaList(location);
            // Check if schema present in the fetched schemaData
            schemaFound = schemaData?.[initialState.database]?.some(
              ({ SCHEMA_NAME }) => SCHEMA_NAME === initialState.schema
            );

            const clusters = await (location !== "SNOWFLAKE"
              ? getExternalSnowflakeCluster(selectedLocation?.value)
              : clusterList);

            // Check if cluster present in the fetched cluster list based on connection
            clusterFound =
              (clusters || [])?.some(({ value }) => value?.toLowerCase() === initialState.cluster?.toLowerCase()) ||
              false;

            // if Schema or cluster not found then show error
            if (!schemaFound) setSchemaError(true);
            if (!clusterFound) setClusterError(true);
          }
        } else {
          initialState.runJobAsUserId = user.User_id;
          initialState.runJobAsUserFullName = currentUserFullName;
          initialState.database = DBName;
          initialState.schema = "PROJECT";
        }

        if (!initialState.runJobAsUserId) {
          initialState.runJobAsUserId = user.User_id;
          initialState.runJobAsUserFullName = currentUserFullName;
        }

        if (inCreateMode || initialState?.location === "SNOWFLAKE") await getWorkspaceParticipants();

        if (!inCreateMode) {
          setPrevState(setNameState, {
            description: initialState.description || "",
            DataPipeline_id: initialState.DataPipeline_id,
          });
          nameTabRef.current = {
            name: initialState.name,
            description: initialState.description || "",
            DataPipeline_id: initialState.DataPipeline_id,
          };

          runTimeTabRef.current = {
            cluster: initialState.cluster,
            runJobAsUserId: initialState?.location === "SNOWFLAKE" ? initialState.runJobAsUserId : null,
          };

          if (
            initialState?.location !== "SNOWFLAKE" ||
            (!inCreateMode &&
              initialState.runJobAsUserId &&
              initialState?.location === "SNOWFLAKE" &&
              !participantsExcludeVisitor.current.some((user) => user.User_id === initialState.runJobAsUserId))
          ) {
            initialState.runJobAsUserId = null;
            initialState.runJobAsUserFullName = "";
          }

          setPrevState(setRunTimeState, {
            cluster: clusterFound ? initialState.cluster : inCreateMode ? defaultCluster : "",
            runJobAsUserId: initialState.runJobAsUserId,
            runJobAsUserFullName: initialState.runJobAsUserFullName,
          });

          setPrevState(setScheduleState, {
            scheduleEnabled: initialState.scheduleEnabled || false,
            nextRunTime: initialState.nextRunTime || "",
            timeZone: initialState.timeZone || timezone,
          });
          scheduleTabRef.current = {
            scheduleEnabled: initialState.scheduleEnabled || false,
            schedule: cronScheduleRef.current,
          };

          transformTabRef.current = {
            location: initialState.location,
            Type: initialState.Type,
            query: initialState.query,
            database: initialState.database,
            schema: initialState.schema,
          };
        }

        // Need to make it as separate as possible
        setPrevState(setTransformJobState, {
          ...initialState,
          ...(!schemaFound ? { database: "", schema: "" } : {}),
        });

        setLoading(false);
      }
      fetchData();
    }
  }, [
    DBName,
    Workspace_id,
    clusterList,
    currentUserFullName,
    defaultCluster,
    dispatch,
    editJob.data.type,
    editJob.id,
    getExternalSnowflakeCluster,
    getLocationList,
    getSchemaList,
    getWorkspaceParticipants,
    inCreateMode,
    navigate,
    setLoading,
    timezone,
    user.User_id,
  ]);

  // For fetching data for Notebook Jobs
  useEffect(() => {
    if (Number(editJob.data.type) === 7) {
      const getTargets = async () => {
        const pipelinesResponse = await new SageMakerService().getPipelines();
        return { pipelineList: pipelinesResponse.data || [] };
      };

      // Make this common for all jobs
      const getNotebookSchedule = async (pipelineList = []) => {
        const wsService = new WorkspaceService();
        const { data, success } = await wsService.getNotebookSchedule(Workspace_id, editJob.id);
        if (!success) return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);

        const { Job_id, status, Type, Name, StartTime, averageRunTimeInMS } = data;
        if (status === "running" || status === "terminating") setValue("Runtime");

        dispatch(
          saveJobDetails({
            Job_id,
            status,
            Type,
            Name,
            StartTime,
            averageRunTimeInMS,
          })
        );

        cronScheduleRef.current = data.Schedule;

        const jobPipeline =
          pipelineList && data.Detail.jobPipeline && pipelineList.some(({ value }) => value === data.Detail.jobPipeline)
            ? data.Detail.jobPipeline
            : "";

        return {
          pipelineList: pipelineList,
          name: data.Name,
          DataPipeline_id: data.DataPipeline_id,
          description: data.Description || "",
          type: data.Detail.type || "sagemaker_pipeline",
          jobPipeline,
          timeZone: data.ServerTimeZone,
          scheduleEnabled: Boolean(data.Enabled),
          nextRunTime: data.nextRun,
        };
      };

      async function fetchData() {
        setLoading(true);
        let initialState = {
          type: "sagemaker_pipeline",
          jobPipeline: "",
          ...(await getTargets()),
        };

        if (!inCreateMode) {
          initialState = { ...initialState, ...(await getNotebookSchedule(initialState.pipelineList)) };
        }

        if (!inCreateMode) {
          setPrevState(setNameState, {
            description: initialState.description || "",
            DataPipeline_id: initialState.DataPipeline_id,
          });
          nameTabRef.current = {
            name: initialState.name,
            description: initialState.description || "",
            DataPipeline_id: initialState.DataPipeline_id,
          };

          setPrevState(setScheduleState, {
            scheduleEnabled: initialState.scheduleEnabled || false,
            nextRunTime: initialState.nextRunTime || "",
            timeZone: initialState.timeZone || timezone,
          });
          scheduleTabRef.current = {
            scheduleEnabled: initialState.scheduleEnabled || false,
            schedule: cronScheduleRef.current,
          };

          notebookTabRef.current = {
            type: initialState.type,
            jobPipeline: initialState.jobPipeline,
          };
        }

        // Need to make it as separate as possible
        setNotebookJobState({
          pipelineList: initialState.pipelineList,
          type: initialState.type,
          jobPipeline: initialState.jobPipeline,
        });

        setLoading(false);
      }
      fetchData();
    }
  }, [Workspace_id, dispatch, editJob.data.type, editJob.id, inCreateMode, navigate, setLoading, timezone]);

  // For fetching data for Notification Jobs
  useEffect(() => {
    const jobType = editJob.data.type;

    if (Number(jobType) === 9) {
      // Make this common for all jobs
      const getNotificationJobSchedule = async () => {
        const wsService = new WorkspaceService();
        const { data, success } = await wsService.getNotificationSchedule(Workspace_id, editJob.id);
        if (!success) return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);

        const { Job_id, status, Type, Name, StartTime, averageRunTimeInMS } = data;
        if (status === "running" || status === "terminating") setValue("Runtime");

        dispatch(
          saveJobDetails({
            Job_id,
            status,
            Type,
            Name,
            StartTime,
            averageRunTimeInMS,
          })
        );

        cronScheduleRef.current = data.Schedule;

        const additionalDataSections = (
          data.Detail?.additionalDataConfig ?? [{ key: FIRST_KEY, variable: "$ADDITIONAL_DATA$", ...addNewSection }]
        ).map((item) => {
          const query = decodeURIComponent(item.query || "");

          return { ...item, query };
        });

        return {
          name: data.Name,
          DataPipeline_id: data.DataPipeline_id,

          timeZone: data.ServerTimeZone,
          scheduleEnabled: data.Enabled,
          nextRunTime: data.nextRun,

          subject: data.Detail?.subject || "",
          body: await base64Decode(data.Detail?.body || ""),
          emails: data.Detail?.emails || [],

          additionalDataSections,
        };
      };

      async function fetchData() {
        setLoading(true);

        const [locationList] = await Promise.all([
          getLocationList(false, ["snowflake"]),
          getWorkspaceParticipants(), // No need for variable
          getSchemaList("SNOWFLAKE"), // No need for variable
        ]);

        let initialState = { ...locationList };
        locationClustersRef.current["SNOWFLAKE"] = clusterList;

        if (!inCreateMode) {
          initialState = { ...initialState, ...(await getNotificationJobSchedule()) };

          for (const section of initialState.additionalDataSections) {
            const selectedLocationType =
              locationList.locationList.find(({ value }) => value === section?.location)?.type || "snowflake";

            if (selectedLocationType === "snowflake") {
              const schemasByDB = await getSchemaList(section.location);
              const isSchemaExist = checkIsSchemaExist(schemasByDB, section.database, section.schema);

              if (!isSchemaExist) {
                section.database = "";
                section.schema = "";
              }

              let clusters = [];
              if (section.location === "SNOWFLAKE") clusters = clusterList || [];
              else if (selectedLocationType === "snowflake") {
                clusters = await getExternalSnowflakeCluster(section.location);
              }

              const selectedClusterName =
                section.Cluster || (selectedLocationType === "snowflake" ? defaultCluster : "");

              // Check existence
              const isClusterExist = checkIsClusterExist(clusters, selectedClusterName);
              section.Cluster = (isClusterExist ? section.Cluster : defaultCluster) ?? "";

              setPrevState(setVariableError, {
                [section.key]: {
                  message: "",
                  schemaError: !isSchemaExist,
                  userNotExist: !participantsExcludeVisitor.current.some((_user) => {
                    return _user.User_id === section.runJobAsUserId;
                  }),
                  clusterError: !isClusterExist,
                },
              });
            }

            if (!section.runJobAsUserId) {
              section.runJobAsUserId = user.User_id;
              section.runJobAsUserFullName = currentUserFullName;
            }
          }
        } else {
          initialState.additionalDataSections = [
            { ...addNewSection, key: FIRST_KEY, variable: "$ADDITIONAL_DATA$", fileName: "Exported_Data" },
          ];

          setPrevState(setVariableError, {
            [FIRST_KEY]: { error: false, message: "", schemaError: false, userNotExist: false },
          });
        }

        if (!inCreateMode) {
          setPrevState(setNameState, {
            description: initialState.description || "",
            DataPipeline_id: initialState.DataPipeline_id,
          });

          nameTabRef.current = {
            name: initialState.name,
            description: initialState.description || "",
            DataPipeline_id: initialState.DataPipeline_id,
          };

          setPrevState(setScheduleState, {
            scheduleEnabled: initialState.scheduleEnabled || false,
            nextRunTime: initialState.nextRunTime || "",
            timeZone: initialState.timeZone || timezone,
          });

          scheduleTabRef.current = {
            scheduleEnabled: initialState.scheduleEnabled || false,
            schedule: cronScheduleRef.current,
          };

          notificationTabRef.current = {
            subject: initialState.subject,
            emails: initialState.emails,
            body: initialState.body,
          };
        }

        initialState.additionalDataSections.forEach((section) => {
          additionalDataSectionsRef.current[section.key] = {
            runJobAsUserId: section.runJobAsUserId || "",
            location: section.location || "SNOWFLAKE",
            database: section.database || "",
            schema: section.schema || "",
            Cluster: section.Cluster || defaultCluster,
            includeResult: section.includeResult || false,
            queryResultAs: section.queryResultAs || 1,
            query: section.query || "",
            fileName: section.fileName || "",
          };
        });

        setSelectedKey(initialState.additionalDataSections[0].key);
        setAdditionalDataKeys(initialState.additionalDataSections);

        // Need to make it as separate as possible
        setPrevState(setNotificationState, {
          ...initialState,
          ...(!false ? { database: "", schema: "" } : {}),
        });

        setLoading(false);
      }
      fetchData();
    }
  }, [
    DBName,
    Workspace_id,
    addNewSection,
    clusterList,
    currentUserFullName,
    defaultCluster,
    defaultSchema,
    dispatch,
    editJob.data.type,
    editJob.id,
    getExternalSnowflakeCluster,
    getLocationList,
    getSchemaList,
    getWorkspaceParticipants,
    inCreateMode,
    navigate,
    setLoading,
    timezone,
    user.User_id,
  ]);

  const getObjects = useCallback(
    async (type, jobType, Job_id) => {
      const datasource = new DataSource();
      const { data, success } = await datasource.getObjectNamesByType(
        Workspace_id,
        OBJECT_TYPE[type],
        jobType,
        Job_id !== "NaN" ? Job_id : null
      );

      if (!success) return [];
      if (jobType === 1) return data?.length ? data.filter(({ datasetType }) => datasetType !== "readonly") : [];
      if (OBJECT_TYPE[type] === 2 || jobType !== 4) return data || [];
      return data || [];
    },
    [Workspace_id]
  );

  // For notification Job
  useEffect(() => {
    additionalDataSections.forEach(({ key, includeResult, queryResultAs, variable }) => {
      const lowerVar = variable?.toLowerCase();
      const isMissingVariable = includeResult && queryResultAs === 2 && !variable;
      const isDuplicate = lowerVar && variableNames.filter((name) => name === lowerVar).length > 1;
      setVariableError((prev) => ({ ...prev, [key]: { ...prev[key], isMissingVariable, isDuplicate } }));
    });
  }, [additionalDataSections, variableNames]);

  // For fetching data for Data Modeling Jobs
  useEffect(() => {
    if (DATA_MODELING_JOBS.includes(Number(editJob.data.type))) {
      const getDataModelingJobSchedule = async () => {
        const wsService = new WorkspaceService();
        const { data, success } = await wsService.getDataModelingJobSchedule(
          Workspace_id,
          editJob.id,
          editJob.data.type
        );
        if (!success) return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);

        const { Job_id, status, Type, Name, StartTime, averageRunTimeInMS } = data;
        if (status === "running" || status === "terminating") setValue("Runtime");

        dispatch(
          saveJobDetails({
            Job_id,
            status,
            Type,
            Name,
            StartTime,
            averageRunTimeInMS,
          })
        );

        cronScheduleRef.current = data.Schedule;

        return data;
      };

      (async () => {
        setLoading(true);
        let jobDetails = {};

        if (!inCreateMode) {
          jobDetails = await getDataModelingJobSchedule();

          if (!jobDetails) return;

          const {
            Name: name,
            DataPipeline_id = -1,
            Description: description = "",
            Enabled: scheduleEnabled = false,
            nextRun: nextRunTime = "",
            ServerTimeZone: timeZone = timezone,
            Schedule,
            Detail: { Cluster: cluster },
            Type,
          } = jobDetails;

          setPrevState(setNameState, {
            name,
            DataPipeline_id,
            description: description || "",
          });
          nameTabRef.current = {
            name,
            DataPipeline_id,
            description: description || "",
          };

          setPrevState(setScheduleState, {
            scheduleEnabled: Boolean(scheduleEnabled),
            nextRunTime,
            timeZone,
          });
          cronScheduleRef.current = Schedule;
          scheduleTabRef.current = { scheduleEnabled: Boolean(scheduleEnabled), schedule: Schedule };

          if (Type === 4 && jobDetails.Detail.query) {
            setPrevState(setDataQualityJobState, { query: jobDetails.Detail.query });
            queryTabRef.current.query = jobDetails.Detail.query;
          }

          //Setting selected cluster
          if ((clusterList || [])?.some((clst) => clst.value === cluster)) setPrevState(setRunTimeState, { cluster });
          runTimeTabRef.current = { cluster };
        }

        //Other details
        const [objectList] = await Promise.all([
          getObjects(
            [5, 6].includes(Number(editJob.data.type)) ? "Entity" : "DataSource",
            Number(editJob.data.type),
            Number(editJob.id)
          ),
        ]);

        //Setting selected refresh data sources
        if (objectList.length) {
          let stateToSet = [];
          if (jobDetails?.DataSource?.selectAll === true)
            stateToSet = objectList.map((item) => ({ ...item, checked: true }));
          else {
            const typeOfObject = [5, 6].includes(Number(editJob.data.type)) ? "entities" : "dataSources";
            const selectedSources = jobDetails?.DataSource?.[typeOfObject] || [] || [];
            stateToSet = objectList.map((item) => ({ ...item, checked: selectedSources.includes(item.name) }));
          }
          setDataSets(stateToSet);
          datasetTabRef.current = stateToSet;
        }
        setLoading(false);
      })();
    }
  }, [
    Workspace_id,
    clusterList,
    dispatch,
    editJob.data.type,
    editJob.id,
    getObjects,
    inCreateMode,
    navigate,
    setLoading,
    timezone,
  ]);

  useEffect(() => {
    if (!dataPipelineList) {
      const getDataPipelineList = async () => {
        const pipeline = new DataPipelineService();
        const { success, data } = await pipeline.getDataPipelineList(Workspace_id);
        dispatch(handlePipelineList(success && data?.dataPipelineList?.length ? data.dataPipelineList : []));
      };
      getDataPipelineList();
    }
  }, [Workspace_id, dataPipelineList, dispatch]);

  useEffect(() => {
    return () => dispatch(handleEditJob(null));
  }, [dispatch]);

  // Fetching tabs based on JOB_TYPE
  const $tabs = useMemo(() => {
    if (editJob !== null) {
      let tabs = TABS_BY_JOB_TYPE[editJob?.data?.type || 1];
      if (inCreateMode) tabs = tabs.filter((tab) => tab.id !== "History");
      return tabs;
    }
    return [];
  }, [editJob, inCreateMode]);

  const TabComponent = useCallback(() => {
    if (loading) return <></>;
    switch (value) {
      case "Name":
        return <NamePanel state={nameState} setState={setNameState} />;
      case "Schedule":
        return <SchedulePanel state={scheduleState} setState={setScheduleState} cronScheduleRef={cronScheduleRef} />;
      case "Runtime":
        return (
          <RunTimePanel
            restrictOrNot={restrictOrNot}
            handleSave={handleSave}
            state={runTimeState}
            setState={setRunTimeState}
            options={clusterList}
            extSnowflakeClusterList={extSnowflakeClusterList}
            participantsExcludeVisitor={participantsExcludeVisitor.current || []}
            transformJobState={Number(jobType) === 9 ? notificationState : transformJobState}
            clusterError={clusterError}
            isSnowflake={[exchangeJobState.sourceType?.toLowerCase(), destinationType?.toLowerCase()].includes(
              "snowflake"
            )}
            isExternalSnowflake={isExternalSnowflake || false}
          />
        );
      case "History":
        return <JobHistory />;
      case "DataSets":
        return (
          <DataSourceList
            tabType="DataSets"
            state={dataSets}
            setState={setDataSets}
            selectAll={selectAll}
            intermediate={intermediate}
            handleRefresh={getObjects}
            exceptReadOnly={jobType === 1}
          />
        );
      case "DataQuality":
        return (
          <DataQualityQuery
            query={dataQualityJobState.query}
            setQuery={(value) => setPrevState(setDataQualityJobState, { query: value })}
            setQueryError={(value) => setPrevState(setDataQualityJobState, { queryError: value })}
            cluster={runTimeState.cluster ?? defaultCluster}
          />
        );
      case "Entities":
        return (
          <DataSourceList
            tabType="Entities"
            state={dataSets}
            setState={setDataSets}
            selectAll={selectAll}
            intermediate={intermediate}
            handleRefresh={getObjects}
          />
        );
      case "Action":
        return (
          <SourceDestination
            restrictedObject={restrictedObject.current}
            exchangeJobState={exchangeJobState}
            setExchangeJobState={setExchangeJobState}
            setPreviousState={setPreviousState}
            previousState={previousState}
            fieldLoading={fieldLoading}
            loadLocationList={loadLocationList}
            handleJobPurposeChange={handleJobPurposeChange}
            setFieldLoading={setFieldLoading}
            loadSchemasClusters={loadSchemasClusters}
            runTimeState={runTimeState}
          />
        );
      case "Transform":
        return (
          <TransformPanel
            schemas={schemas}
            fieldLoading={fieldLoading}
            schemaError={schemaError}
            setSchemaError={setSchemaError}
            transformJobState={transformJobState}
            setTransformJobState={setTransformJobState}
            runJobAsUserId={runTimeState?.runJobAsUserId}
            getSchemaList={getSchemaList}
            cluster={runTimeState.cluster}
            setRunTimeState={setRunTimeState}
            getExternalSnowflakeCluster={getExternalSnowflakeCluster}
          />
        );

      case "Condition":
        return (
          <RoutingPanel
            schemas={schemas}
            fieldLoading={fieldLoading}
            schemaError={schemaError}
            setSchemaError={setSchemaError}
            transformJobState={transformJobState}
            setTransformJobState={setTransformJobState}
            runJobAsUserId={runTimeState?.runJobAsUserId}
            getSchemaList={getSchemaList}
            cluster={runTimeState.cluster}
            setRunTimeState={setRunTimeState}
            getExternalSnowflakeCluster={getExternalSnowflakeCluster}
          />
        );

      case "Notebook":
        return <NotebookPanel notebookJobState={notebookJobState} setNotebookJobState={setNotebookJobState} />;

      case "EmailContent":
        return <EmailContent jobState={notificationState} setJobState={setNotificationState} />;

      case "AdditionalData":
        return (
          <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", margin: "6px" }}>
              <ButtonUI
                endIcon={<AddIcon />}
                variant="contained"
                color="info"
                style={{ width: "auto" }}
                onClick={generateName}
                disabled={additionalDataSections.length >= 5 || notificationState.loading}
              >
                Add Section
              </ButtonUI>
            </Box>
            <AdditionalDatapanel
              additionalDataSections={additionalDataSections}
              additionalState={additionalDataSections[selectedSectionIndex]}
              getSchemaList={getSchemaList}
              locationClusters={locationClustersRef.current}
              locationSchemas={locationSchemasRef.current}
              locations={notificationState.locationList}
              participantsExcludeVisitor={participantsExcludeVisitor.current}
              selectedKey={selectedKey}
              setAdditionalState={setAdditionalDataKeys}
              setJobState={setNotificationState}
              updateClusterList={(location, clusters) => (locationClustersRef.current[location] = clusters)}
              setSelectedKey={setSelectedKey}
              setVariableError={setVariableError}
              variableError={variableError}
              section={selectedSectionIndex + 1}
            />
          </>
        );

      default:
        return <NamePanel state={nameState} setState={setNameState} />;
    }
  }, [
    loading,
    value,
    nameState,
    scheduleState,
    restrictOrNot,
    handleSave,
    runTimeState,
    clusterList,
    extSnowflakeClusterList,
    jobType,
    notificationState,
    transformJobState,
    clusterError,
    exchangeJobState,
    destinationType,
    isExternalSnowflake,
    dataSets,
    selectAll,
    intermediate,
    getObjects,
    dataQualityJobState.query,
    defaultCluster,
    previousState,
    fieldLoading,
    loadLocationList,
    handleJobPurposeChange,
    loadSchemasClusters,
    schemas,
    schemaError,
    getSchemaList,
    getExternalSnowflakeCluster,
    notebookJobState,
    generateName,
    additionalDataSections,
    selectedSectionIndex,
    selectedKey,
    variableError,
  ]);

  // Using this hook to integrate this particular fucntion inside the ref so that it can be used in parent or sibling components
  useImperativeHandle(ref, () => ({ handleCloseOrReplacePanel, restrictOrNot }));

  return (
    <>
      <Box className={classes.PanelContainer}>
        <Paper elevation={2} className={classes.PanelHeader}>
          <AppBar className={classes.AppBar}>
            <Box className={classes.TabContainer}>
              <Tabs
                value={value}
                onChange={handleChange}
                variant="scrollable"
                scrollButtons={true}
                className={classes.Tabs}
              >
                {$tabs.map(({ id, label }) => (
                  <Tab key={id} label={label} value={id} />
                ))}
              </Tabs>
              {!inCreateMode && (
                <Tooltip
                  placement="bottom"
                  title={<Box sx={{ fontSize: "0.875rem" }}>{`Editing "${editJob.data.label}"`}</Box>}
                  arrow
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", minWidth: "30px", maxWidth: "30px", margin: "0 5px" }}
                  >
                    <InfoOutlinedIcon color="info" />
                  </Box>
                </Tooltip>
              )}

              {[1, 2].includes(workspace?.workspace?.accessRole) && (
                <Box sx={{ minWidth: "90px", maxWidth: "90px", margin: "0 5px" }}>
                  <ButtonUI
                    onClick={() => {
                      if (
                        (jobType === 3 || jobType === 8) &&
                        transformJobState.location === "SNOWFLAKE" &&
                        accessRole === 2 &&
                        !inCreateMode &&
                        compareQuery(transformTabRef.current.query, transformJobState.query) &&
                        runTimeState.runJobAsUserId !== user.User_id
                      )
                        setPrevState(setJobReassignment, { openConfirm: true });
                      else handleSave();
                    }}
                    variant="contained"
                    color="success"
                    disabled={loading}
                  >
                    Save Job
                  </ButtonUI>
                </Box>
              )}
              <IconButton onClick={handleCloseOrReplacePanel}>
                <CloseIcon />
              </IconButton>
            </Box>
          </AppBar>
        </Paper>
        <Box flexGrow={1} sx={{ padding: "15px", maxHeight: "100%", overflowY: "auto" }}>
          {$tabs.map((tab) => (
            <TabPanel key={tab.id} index={tab.id} value={value}>
              {TabComponent()}
            </TabPanel>
          ))}
        </Box>
      </Box>
      {notAllowedDialog.openConfirm && (
        <NotAllowedDialog
          notAllowedDialog={notAllowedDialog}
          onClose={() => {
            setNotAllowedDialog({
              openConfirm: false,
              confirmMessage: "",
              title: "",
            });
          }}
        />
      )}
      {closeConfirmation.open && (
        <ConfirmPopup
          configData={{
            popupTitle: "Close Edit Panel",
            popupMessage: inCreateMode
              ? MESSAGES.EditPanel.CreateUnSavedChanges
              : MESSAGES.EditPanel.EditUnSavedChanges,
            handleCancel: handleCloseConfirmation,
            handleClick: () => handleOkClick(closeConfirmation.nextNodeToEdit || null),
          }}
        />
      )}
      {jobReassignment.openConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: "Job Reassignment",
            popupMessage: (
              <>
                <span>
                  You have edited the query for this job. Saving the job will now result in running the job under your
                  name.
                </span>
                <br />
                <span>Are you sure you want to proceed?</span>
              </>
            ),
            handleCancel: () => setPrevState(setJobReassignment, { openConfirm: false }),
            handleClick: handleSave,
          }}
        />
      )}
    </>
  );
});

export default React.memo(EditPanel);
